import { useState, useEffect } from 'react';
import WizardModal, { WizardButton, WizardLabel, WizardInput, WizardSelect, WizardSuccess, WizardError } from './WizardModal';
import useWizardSubmit from './useWizardSubmit';
import { get, post } from '../../api';
import { formatNumber } from '../../utils/formatNumber';

interface Template {
  id: number;
  name: string;
  default_duration_min: number;
  met_value: number | null;
}

interface EstimateResponse {
  valid: boolean;
  name?: string;
  kcal_burned?: number;
  met?: number;
  duration_min?: number;
  weight_kg?: number;
  message?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

type Mode = 'estimate' | 'manual';
type EstimateStep = 'input' | 'confirm';

const INTENSITY_OPTIONS = [
  { value: 'leicht', label: 'Leicht' },
  { value: 'moderat', label: 'Moderat' },
  { value: 'intensiv', label: 'Intensiv' },
];

export default function ActivityWizard({ open, onClose }: Props) {
  const [mode, setMode] = useState<Mode>('estimate');
  const [templates, setTemplates] = useState<Template[]>([]);
  const { status, error, submit, reset } = useWizardSubmit();

  // Estimate mode
  const [description, setDescription] = useState('');
  const [durationMin, setDurationMin] = useState('');
  const [intensity, setIntensity] = useState('moderat');
  const [estimateStep, setEstimateStep] = useState<EstimateStep>('input');
  const [estimate, setEstimate] = useState<EstimateResponse | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);

  // Manual mode
  const [name, setName] = useState('');
  const [kcalBurned, setKcalBurned] = useState('');
  const [manualDuration, setManualDuration] = useState('');

  // Shared
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      get<Template[]>('/activity-templates').then(setTemplates).catch(() => {});
    }
  }, [open]);

  const metTemplates = templates.filter(t => t.met_value != null);

  // ── Estimate: Step 1 → AI ──
  const handleEstimate = async () => {
    if (!description.trim()) return;
    setEstimating(true);
    setEstimateError(null);
    try {
      const result = await post<EstimateResponse>('/ai/estimate-activity', {
        description: description.trim(),
        duration_min: durationMin ? parseInt(durationMin) : undefined,
        intensity,
      });
      if (!result.valid) {
        setEstimateError(result.message || 'Das scheint keine Aktivität zu sein.');
      } else {
        setEstimate(result);
        setEstimateStep('confirm');
      }
    } catch (err: unknown) {
      setEstimateError((err as { message?: string })?.message || 'Schätzung fehlgeschlagen');
    } finally {
      setEstimating(false);
    }
  };

  // ── Estimate: Step 2 → Save ──
  const handleEstimateConfirm = async () => {
    if (!estimate) return;
    await submit('/activity', {
      name: estimate.name,
      kcal_burned: estimate.kcal_burned,
      duration_min: estimate.duration_min,
      intensity,
      date,
      note: note || undefined,
    });
  };

  // ── Manual save ──
  const handleManualSubmit = async () => {
    if (!name.trim() || !kcalBurned.trim()) return;
    await submit('/activity', {
      name: name.trim(),
      kcal_burned: parseFloat(kcalBurned),
      duration_min: manualDuration ? parseInt(manualDuration) : undefined,
      date,
      note: note || undefined,
    });
  };

  const applyTemplate = (t: Template) => {
    setDescription(t.name);
    setDurationMin(String(t.default_duration_min));
  };

  const handleClose = () => {
    setDescription(''); setDurationMin(''); setIntensity('moderat');
    setEstimateStep('input'); setEstimate(null); setEstimateError(null);
    setName(''); setKcalBurned(''); setManualDuration('');
    setDate(todayISO()); setNote('');
    reset(); onClose();
  };

  if (status === 'success') {
    return (
      <WizardModal open={open} onClose={handleClose} title="Aktivität" footer={null}>
        <WizardSuccess message="Aktivität gespeichert!" onDone={handleClose} />
      </WizardModal>
    );
  }

  // ── Estimate: Confirm step ──
  if (mode === 'estimate' && estimateStep === 'confirm' && estimate) {
    return (
      <WizardModal open={open} onClose={handleClose} title="Stimmt das?"
        footer={
          <div className="flex gap-2">
            <button onClick={() => { setEstimateStep('input'); setEstimate(null); }}
              className="flex-1 py-3 bg-warm-100 text-warm-600 rounded-xl font-medium text-sm">
              Nein, zurück
            </button>
            <WizardButton onClick={handleEstimateConfirm} loading={status === 'loading'}>
              Speichern
            </WizardButton>
          </div>
        }
      >
        {error && <WizardError message={error} />}

        <div className="bg-teal-50 rounded-xl p-4 text-center">
          <p className="text-sm font-medium text-teal-800">{estimate.name}</p>
          <p className="font-display text-3xl font-semibold text-teal-800 mt-2">~{formatNumber(estimate.kcal_burned || 0)}</p>
          <p className="text-xs text-teal-600">kcal verbrannt</p>
          <p className="text-[10px] text-teal-400 mt-2">
            {estimate.duration_min} Min · MET {estimate.met} · {formatNumber(estimate.weight_kg || 0)} kg · {intensity}
          </p>
        </div>

        <div>
          <WizardLabel>Datum</WizardLabel>
          <WizardInput type="date" value={date} onChange={setDate} />
        </div>
        <div>
          <WizardLabel>Notiz (optional)</WizardLabel>
          <WizardInput value={note} onChange={setNote} placeholder="z.B. Morgenrunde" />
        </div>
      </WizardModal>
    );
  }

  // ── Main view ──
  return (
    <WizardModal
      open={open}
      onClose={handleClose}
      title="Aktivität loggen"
      footer={
        mode === 'estimate' ? (
          <WizardButton onClick={handleEstimate} loading={estimating} disabled={!description.trim()}>
            Kalorien schätzen
          </WizardButton>
        ) : (
          <WizardButton onClick={handleManualSubmit} loading={status === 'loading'} disabled={!name.trim() || !kcalBurned.trim()}>
            Speichern
          </WizardButton>
        )
      }
    >
      {(error || estimateError) && <WizardError message={estimateError || error || ''} />}

      {/* Mode Toggle */}
      <div className="flex bg-warm-100 rounded-lg p-0.5">
        <button onClick={() => setMode('estimate')} className={`flex-1 py-2 rounded-md text-xs font-medium transition-colors ${mode === 'estimate' ? 'bg-white text-teal-600 shadow-sm' : 'text-warm-500'}`}>
          Schätzen
        </button>
        <button onClick={() => setMode('manual')} className={`flex-1 py-2 rounded-md text-xs font-medium transition-colors ${mode === 'manual' ? 'bg-white text-teal-600 shadow-sm' : 'text-warm-500'}`}>
          Manuell
        </button>
      </div>

      {mode === 'estimate' ? (
        <>
          <div>
            <WizardLabel>Was hast du gemacht?</WizardLabel>
            <WizardInput value={description} onChange={setDescription}
              placeholder="z.B. Joggen, Badminton, Yoga..." autoFocus />
          </div>

          {metTemplates.length > 0 && (
            <div>
              <WizardLabel>Schnellauswahl</WizardLabel>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {metTemplates.slice(0, 8).map(t => (
                  <button key={t.id} onClick={() => applyTemplate(t)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      description === t.name ? 'bg-teal-400 text-white' : 'bg-warm-100 text-warm-600 hover:bg-teal-50'
                    }`}>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <WizardLabel>Dauer (Min)</WizardLabel>
              <WizardInput value={durationMin} onChange={setDurationMin} placeholder="30" type="number" min="1" />
            </div>
            <div>
              <WizardLabel>Intensität</WizardLabel>
              <WizardSelect value={intensity} onChange={setIntensity} options={INTENSITY_OPTIONS} />
            </div>
          </div>
        </>
      ) : (
        <>
          <div>
            <WizardLabel>Aktivität</WizardLabel>
            <WizardInput value={name} onChange={setName} placeholder="z.B. Spaziergang" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <WizardLabel>Kalorien verbrannt</WizardLabel>
              <WizardInput value={kcalBurned} onChange={setKcalBurned} placeholder="200" type="number" min="1" />
            </div>
            <div>
              <WizardLabel>Dauer (optional)</WizardLabel>
              <WizardInput value={manualDuration} onChange={setManualDuration} placeholder="30" type="number" min="1" />
            </div>
          </div>
        </>
      )}

      <div>
        <WizardLabel>Datum</WizardLabel>
        <WizardInput type="date" value={date} onChange={setDate} />
      </div>
      <div>
        <WizardLabel>Notiz (optional)</WizardLabel>
        <WizardInput value={note} onChange={setNote} placeholder="z.B. Laufrunde im Park" />
      </div>
    </WizardModal>
  );
}

function todayISO(): string {
  return new Date().toLocaleDateString('sv-SE');
}
